import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusBadge = (status) => {
  const map = {
    "Finished": "bg-green-100 text-green-700",
    "Started": "bg-prepify-100 text-prepify-700",
    "Paused": "bg-amber-100 text-amber-700",
    "Not Started": "bg-slate-100 text-slate-600",
  };
  const label = status === "Finished" ? "Done" : status === "Started" ? "In Progress" : status === "Paused" ? "Paused" : "Pending";
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${map[status] || map["Not Started"]}`}>
      {label}
    </span>
  );
};

const SectionAssignModal = ({ open, onClose, order, employees, onSaved }) => {
  const [sections, setSections] = useState([]); // [{section_name}]
  const [assignments, setAssignments] = useState({}); // section_name -> user_id
  const [existingBySection, setExistingBySection] = useState({}); // section_name -> sub_assignment (includes status)
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !order) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Resolve template
        const tplRes = await axios.get(`${API}/section-templates/resolve`, {
          params: { exam: order.exam, product_type: order.product_type },
        });
        const tplSections = tplRes.data.sections || [];

        // Existing sub-assignments on this order
        const existing = order.sub_assignments || [];
        const existingMap = {};
        existing.forEach((sa) => { existingMap[sa.section_name] = sa; });

        if (cancelled) return;

        // Pre-fill
        const asMap = {};
        tplSections.forEach((s) => { asMap[s] = existingMap[s]?.assigned_to || ""; });
        // Include any existing sections not in template (edge case)
        existing.forEach((sa) => {
          if (!tplSections.includes(sa.section_name)) {
            tplSections.push(sa.section_name);
            asMap[sa.section_name] = sa.assigned_to || "";
          }
        });

        setSections(tplSections.map((name) => ({ section_name: name })));
        setAssignments(asMap);
        setExistingBySection(existingMap);
      } catch (e) {
        toast.error("Failed to load sections");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, order]);

  const progress = useMemo(() => {
    const existing = order?.sub_assignments || [];
    const done = existing.filter((s) => s.task_status === "Finished").length;
    return { done, total: existing.length || sections.length };
  }, [order, sections]);

  const assignedUsersForDisplay = useMemo(() => {
    const ids = Array.from(new Set(Object.values(assignments).filter(Boolean)));
    return ids.map((id) => employees.find((e) => e.id === id)).filter(Boolean);
  }, [assignments, employees]);

  const save = async () => {
    if (!order) return;
    const payload = {
      assignments: sections.map((s) => ({
        section_name: s.section_name,
        assigned_to: assignments[s.section_name] || null,
      })),
    };
    setSaving(true);
    try {
      const res = await axios.put(`${API}/orders/${order.id}/sub-assignments`, payload);
      toast.success("Assignments saved");
      onSaved && onSaved(res.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save assignments");
    } finally {
      setSaving(false);
    }
  };

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="section-assign-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>Assign Sections</span>
            <span className="text-xs font-normal text-slate-500">
              · {order.exam} {order.product_type} · {order.particulars}
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Summary row */}
        <div className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-3 mb-2">
          <div className="flex items-center gap-3 text-sm">
            <span className="font-medium text-slate-700">Progress:</span>
            <span className="text-prepify-700 font-semibold" data-testid="section-progress">
              {progress.done}/{progress.total}
            </span>
            <div className="h-1.5 w-32 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-prepify-600" style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} />
            </div>
          </div>
          {assignedUsersForDisplay.length > 0 && (
            <div className="text-xs text-slate-500">
              {assignedUsersForDisplay.slice(0, 3).map((u) => u.name).join(", ")}
              {assignedUsersForDisplay.length > 3 && ` +${assignedUsersForDisplay.length - 3}`}
            </div>
          )}
        </div>

        {loading ? (
          <div className="py-12 text-center text-sm text-slate-500">Loading sections…</div>
        ) : (
          <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg">
            <div className="grid grid-cols-[1fr_2fr_auto] gap-3 px-4 py-2 bg-slate-50 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              <div>Section</div>
              <div>Writer</div>
              <div>Status</div>
            </div>
            {sections.map((s) => {
              const existing = existingBySection[s.section_name];
              return (
                <div key={s.section_name} className="grid grid-cols-[1fr_2fr_auto] gap-3 items-center px-4 py-3" data-testid={`section-row-${s.section_name}`}>
                  <div className="font-medium text-slate-800">{s.section_name}</div>
                  <div>
                    <Select
                      value={assignments[s.section_name] || "__unassigned__"}
                      onValueChange={(v) => setAssignments((prev) => ({ ...prev, [s.section_name]: v === "__unassigned__" ? "" : v }))}
                    >
                      <SelectTrigger className="h-9" data-testid={`section-writer-${s.section_name}`}>
                        <SelectValue placeholder="Select writer" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__unassigned__">Unassigned</SelectItem>
                        {employees.map((e) => (
                          <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>{statusBadge(existing?.task_status || "Not Started")}</div>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button type="button" variant="outline" onClick={onClose} data-testid="cancel-sections-btn">
            Cancel
          </Button>
          <Button type="button" onClick={save} disabled={saving || loading} data-testid="save-sections-btn">
            {saving ? "Saving…" : "Save assignments"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SectionAssignModal;
